// workAreas/hooks/useGeneralInfo.js
import { useState, useRef } from 'react';
import { updateManagementWorkArea } from 'api';

export const useGeneralInfo = (allWorkAreas, showSnackbar) => {
  const [generalInfoFormData, setGeneralInfoFormData] = useState({});
  const [originalGeneralInfoData, setOriginalGeneralInfoData] = useState({});
  const [generalInfoHasChanges, setGeneralInfoHasChanges] = useState({});
  const [isSavingGeneralInfo, setIsSavingGeneralInfo] = useState(false);
  const isSavingRef = useRef(false);

  const handleGeneralInfoFormChange = (workAreaSlug, newFormData) => {
    setGeneralInfoFormData(prev => ({
      ...prev,
      [workAreaSlug]: newFormData
    }));

    // Check if there are changes
    const originalData = originalGeneralInfoData[workAreaSlug];
    const hasChanges = originalData ? JSON.stringify(newFormData) !== JSON.stringify(originalData) : false;

    setGeneralInfoHasChanges(prev => ({
      ...prev,
      [workAreaSlug]: hasChanges
    }));
  };

  const handleSaveGeneralInfo = async (workAreaSlug) => {
    const formData = generalInfoFormData[workAreaSlug];
    if (!formData) return;

    try {
      setIsSavingGeneralInfo(true);
      isSavingRef.current = true;

      const workArea = allWorkAreas.find(wa => wa.slug === workAreaSlug);
      if (!workArea) {
        throw new Error("Work area not found");
      }

      const requestData = {
        work_area: {
          slug: workAreaSlug,
          name: formData.name,
          description: formData.description,
          status: formData.status,
          resources: formData.capacity,
        },
        resources: {
          type: 'HD',
          size: formData.capacity || 0
        }
      };

      const result = await updateManagementWorkArea(requestData, workAreaSlug);

      if (result && result.success) {
        showSnackbar("General information saved successfully", "success");

        // Update original data to match current form data
        setOriginalGeneralInfoData(prev => ({
          ...prev,
          [workAreaSlug]: { ...formData }
        }));

        // Clear changes state
        setGeneralInfoHasChanges(prev => ({
          ...prev,
          [workAreaSlug]: false
        }));

      } else {
        showSnackbar(result?.error || "Failed to save general information", "error");
      }
    } catch (error) {
      console.error("Error saving general information:", error);
      showSnackbar("Failed to save general information: " + error.message, "error");
    } finally {
      setIsSavingGeneralInfo(false);
      isSavingRef.current = false;
    }
  };

  const handleCancelGeneralInfo = (workAreaSlug) => {
    const originalData = originalGeneralInfoData[workAreaSlug];
    if (originalData) {
      setGeneralInfoFormData(prev => ({
        ...prev,
        [workAreaSlug]: { ...originalData }
      }));

      setGeneralInfoHasChanges(prev => ({
        ...prev,
        [workAreaSlug]: false
      }));
    }
  };

  const initializeGeneralInfo = (workAreas) => {
    const newGeneralInfoFormData = {};
    const newOriginalGeneralInfoData = {};

    workAreas.forEach(workArea => {
      const generalInfoData = {
        name: workArea.name || '',
        description: workArea.description || '',
        capacity: parseInt(workArea?.hot_desks?.length || 0),
        status: workArea.status || 'AC'
      };

      if (!generalInfoFormData[workArea.slug]) {
        newGeneralInfoFormData[workArea.slug] = generalInfoData;
        newOriginalGeneralInfoData[workArea.slug] = { ...generalInfoData };
      } else {
        newGeneralInfoFormData[workArea.slug] = generalInfoFormData[workArea.slug];
        newOriginalGeneralInfoData[workArea.slug] = originalGeneralInfoData[workArea.slug];
      }
    });

    setGeneralInfoFormData(newGeneralInfoFormData);
    setOriginalGeneralInfoData(newOriginalGeneralInfoData);
  };

  return {
    generalInfoFormData,
    generalInfoHasChanges,
    isSavingGeneralInfo,
    isSavingRef,
    handleGeneralInfoFormChange,
    handleSaveGeneralInfo,
    handleCancelGeneralInfo,
    initializeGeneralInfo
  };
};
